// Client copy of the move rules in services/dijon-backend/api/helpers/backgammon/engine.py.
// Only used to highlight what's legal while you build a turn; the server
// re-validates every submitted play.
//
// A side is an array of 26 counts from its own point of view: 1..24 points,
// 25 the bar, 0 borne off. My point p is the opponent's 25 - p.

export const BAR = 25;
export const OFF = 0;

export function initialSide() {
  const s = new Array(26).fill(0);
  s[24] = 2; s[13] = 5; s[8] = 3; s[6] = 5;
  return s;
}

export function pipCount(side) {
  let t = 0;
  for (let i = 1; i < 26; i++) t += i * side[i];
  return t;
}

function allHome(side) {
  for (let i = 7; i < 26; i++) if (side[i]) return false;
  return true;
}

export function singleMoves(me, opp, die) {
  if (me[BAR]) {
    const to = BAR - die;
    return opp[25 - to] >= 2 ? [] : [[BAR, to]];
  }
  const out = [];
  const home = allHome(me);
  for (let frm = 24; frm >= 1; frm--) {
    if (!me[frm]) continue;
    const to = frm - die;
    if (to >= 1) {
      if (opp[25 - to] < 2) out.push([frm, to]);
    } else if (home) {
      if (to === 0) out.push([frm, OFF]);
      else {
        let higher = false;
        for (let p = frm + 1; p <= 6; p++) if (me[p]) { higher = true; break; }
        if (!higher) out.push([frm, OFF]);
      }
    }
  }
  return out;
}

export function applyMove(me, opp, frm, to) {
  const m = me.slice();
  const o = opp.slice();
  m[frm] -= 1;
  m[to] += 1;
  let hit = false;
  if (to !== OFF && o[25 - to] === 1) {
    o[25 - to] = 0;
    o[BAR] += 1;
    hit = true;
  }
  return [m, o, hit];
}

export function diceList(dice) {
  const [a, b] = dice;
  return a === b ? [a, a, a, a] : [a, b];
}

function maxUsable(me, opp, remaining, cache) {
  if (!remaining.length) return 0;
  const key = me.join(',') + '|' + opp.join(',') + '|' + remaining.slice().sort().join(',');
  if (cache.has(key)) return cache.get(key);
  let best = 0;
  const tried = new Set();
  for (let i = 0; i < remaining.length; i++) {
    const die = remaining[i];
    if (tried.has(die)) continue;
    tried.add(die);
    const rest = remaining.slice(0, i).concat(remaining.slice(i + 1));
    for (const [frm, to] of singleMoves(me, opp, die)) {
      const [m2, o2] = applyMove(me, opp, frm, to);
      best = Math.max(best, 1 + maxUsable(m2, o2, rest, cache));
      if (best === remaining.length) {
        cache.set(key, best);
        return best;
      }
    }
  }
  cache.set(key, best);
  return best;
}

export function maxDiceUsable(me, opp, dice) {
  return maxUsable(me, opp, diceList(dice), new Map());
}

function largerDieForced(me, opp, dice, total) {
  const [a, b] = dice;
  if (a === b || total !== 1) return null;
  const hi = Math.max(a, b);
  return singleMoves(me, opp, hi).length ? hi : null;
}

// Legal [frm, to, die] for the next step, given `played` from the turn start.
export function legalNextMoves(me, opp, dice, played) {
  const remaining = diceList(dice);
  let m = me, o = opp;
  for (const [frm, to, die] of played) {
    remaining.splice(remaining.indexOf(die), 1);
    [m, o] = applyMove(m, o, frm, to);
  }
  if (!remaining.length) return [];
  const cache = new Map();
  const total = maxUsable(me, opp, diceList(dice), cache);
  const need = total - played.length;
  if (need <= 0) return [];
  const forced = largerDieForced(me, opp, dice, total);
  const out = [];
  const dies = Array.from(new Set(remaining)).sort((x, y) => y - x);
  for (const die of dies) {
    if (forced !== null && die !== forced) continue;
    const rest = remaining.slice();
    rest.splice(rest.indexOf(die), 1);
    for (const [frm, to] of singleMoves(m, o, die)) {
      const [m2, o2] = applyMove(m, o, frm, to);
      if (1 + maxUsable(m2, o2, rest, cache) >= need) out.push([frm, to, die]);
    }
  }
  return out;
}

// Every destination reachable by moving the checker on `frm` one or more
// steps this turn, mapped to the move sequence that gets there (fewest steps).
export function destinationsFrom(me, opp, dice, played, frm) {
  const dests = new Map();
  const queue = [[played, frm, []]];
  while (queue.length) {
    const [p, at, seq] = queue.shift();
    for (const mv of legalNextMoves(me, opp, dice, p)) {
      if (mv[0] !== at) continue;
      const nseq = seq.concat([mv]);
      if (!dests.has(mv[1])) dests.set(mv[1], nseq);
      // Keep walking the same checker (not past bearing off).
      if (mv[1] !== OFF) queue.push([p.concat([mv]), mv[1], nseq]);
    }
  }
  return dests;
}

export function applyMoves(me, opp, moves) {
  let m = me, o = opp;
  for (const [frm, to] of moves) [m, o] = applyMove(m, o, frm, to);
  return [m, o];
}

// Every distinct resulting position, with one move sequence that reaches it
// (mirrors legal_plays in engine.py). Exactly one entry => the turn is truly
// forced: there is only one position you can end up in, so it can be auto-played.
export function legalPlays(me, opp, dice) {
  const total = maxDiceUsable(me, opp, dice);
  if (total === 0) return [{ moves: [], m: me.slice(), o: opp.slice() }];
  const seen = new Map();
  const rec = (played) => {
    if (played.length === total) {
      const [m, o] = applyMoves(me, opp, played);
      const key = `${m.join(',')}|${o.join(',')}`;
      if (!seen.has(key)) seen.set(key, { moves: played, m, o });
      return;
    }
    for (const mv of legalNextMoves(me, opp, dice, played)) rec(played.concat([mv]));
  };
  rec([]);
  return Array.from(seen.values());
}

export function diceRemaining(dice, played) {
  if (!dice) return [];
  const rem = diceList(dice);
  for (const mv of played) {
    const i = rem.indexOf(mv[2]);
    if (i >= 0) rem.splice(i, 1);
  }
  return rem;
}
